import React, { useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info';
  title?: string;
  message: string;
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  return (
    <div style={{
      position: 'fixed',
      bottom: '1.5rem',
      right: '1.5rem',
      zIndex: 200,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      maxWidth: '380px',
      width: '100%',
      pointerEvents: 'none',
    }}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ toast: ToastMessage; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, 4000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const getTheme = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'var(--bg-surface)',
          border: '1px solid #10b981',
          icon: <CheckCircle2 size={18} style={{ color: '#10b981' }} />,
          accent: '#10b981',
        };
      case 'error':
        return {
          bg: 'var(--bg-surface)',
          border: '1px solid #ef4444',
          icon: <AlertCircle size={18} style={{ color: '#ef4444' }} />,
          accent: '#ef4444',
        };
      case 'info':
      default:
        return {
          bg: 'var(--bg-surface)',
          border: '1px solid var(--accent-primary)',
          icon: <Info size={18} style={{ color: 'var(--accent-primary)' }} />,
          accent: 'var(--accent-primary)',
        };
    }
  };

  const theme = getTheme();

  return (
    <div style={{
      pointerEvents: 'auto',
      background: theme.bg,
      border: theme.border,
      borderRadius: '10px',
      padding: '0.85rem 1rem',
      boxShadow: 'var(--shadow-lg)',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      animation: 'fadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
    }}>
      <div style={{ marginTop: '0.1rem', flexShrink: 0 }}>
        {theme.icon}
      </div>

      <div style={{ flex: 1 }}>
        {toast.title && (
          <h4 style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.15rem' }}>
            {toast.title}
          </h4>
        )}
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
          {toast.message}
        </p>
      </div>

      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-light)',
          cursor: 'pointer',
          padding: '0.1rem',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <X size={15} />
      </button>
    </div>
  );
};

// Custom Dialog Modal for Confirmations / Critical Alerts
interface CustomDialogProps {
  isOpen: boolean;
  type?: 'success' | 'error' | 'info';
  title: string;
  message: string;
  confirmText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

export const CustomDialog: React.FC<CustomDialogProps> = ({
  isOpen,
  type = 'info',
  title,
  message,
  confirmText = 'Got it',
  onConfirm,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    if (type === 'success') return <CheckCircle2 size={28} style={{ color: '#10b981' }} />;
    if (type === 'error') return <AlertCircle size={28} style={{ color: '#ef4444' }} />;
    return <Info size={28} style={{ color: 'var(--accent-primary)' }} />;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(6px)',
      zIndex: 210,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      animation: 'fadeIn 0.2s ease-out',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'var(--bg-surface)',
        borderRadius: '14px',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-xl)',
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <div style={{
            width: '46px',
            height: '46px',
            borderRadius: '12px',
            background: 'var(--bg-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            {getIcon()}
          </div>
          <div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-main)' }}>
              {title}
            </h3>
          </div>
        </div>

        <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {message}
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button
            onClick={onConfirm}
            style={{
              padding: '0.55rem 1.25rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--accent-primary)',
              color: '#ffffff',
              fontSize: '0.88rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
